'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useFeatureFlags } from '@kit/feature-flags';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { JWTUserData } from '@kit/supabase/types';
import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import {
  buildAuthUrlForApply,
  type ApplyJourneyTarget,
} from '~/_lib/apply-journey';
import { LanguageSubMenu } from '~/components/language-sub-menu';
import { LocaleSelector } from '~/components/locale-selector';
import pathsConfig from '~/config/paths.config';

/**
 * Detect when the header is rendered on a competition / audition detail page
 * so the global Sign in / Sign up buttons can carry the apply journey instead
 * of dropping it. Without this, an anonymous visitor who clicks the header
 * (rather than the in-page apply CTA) signs up with no `next`/`type=dancer`,
 * gets routed through the `/welcome` picker, and loses the competition they
 * were registering for.
 */
function matchApplySurface(pathname: string): ApplyJourneyTarget | null {
  const competition = pathname.match(/^\/competitions\/([^/]+)$/);
  if (competition?.[1]) {
    return { surface: 'competition', slug: competition[1] };
  }
  const audition = pathname.match(/^\/auditions\/([^/]+)$/);
  if (audition?.[1]) {
    return { surface: 'audition', slug: audition[1] };
  }
  return null;
}

const ModeToggle = dynamic(
  () =>
    import('@kit/ui/mode-toggle').then((mod) => ({
      default: mod.ModeToggle,
    })),
  { ssr: false },
);

const MobileModeToggle = dynamic(
  () =>
    import('@kit/ui/mobile-mode-toggle').then((mod) => ({
      default: mod.MobileModeToggle,
    })),
  { ssr: false },
);

const paths = {
  home: pathsConfig.app.home,
};

export function SiteHeaderAccountSection({
  user,
  selectedLocale,
}: {
  user: JWTUserData | null;
  /** Explicit `lang` cookie choice read by the server parent; `null` = Automatic. */
  selectedLocale: string | null;
}) {
  const signOut = useSignOut();
  const featureFlags = useFeatureFlags();

  const features = {
    enableThemeToggle: featureFlags.enableThemeToggle,
  };

  if (user) {
    return (
      <PersonalAccountDropdown
        showProfileName={false}
        paths={paths}
        features={features}
        user={user}
        signOutRequested={() => signOut.mutateAsync()}
        extraItems={<LanguageSubMenu selectedLocale={selectedLocale} />}
      />
    );
  }

  return (
    <AuthButtons
      enableThemeToggle={featureFlags.enableThemeToggle}
      selectedLocale={selectedLocale}
    />
  );
}

function AuthButtons({
  enableThemeToggle,
  selectedLocale,
}: {
  enableThemeToggle: boolean;
  selectedLocale: string | null;
}) {
  const pathname = usePathname();
  const applyTarget = matchApplySurface(pathname);

  // On an apply surface, preserve the journey; elsewhere use the plain
  // auth paths. Sign-up additionally carries `type=dancer` so the layout
  // guard skips the `/welcome` picker for an explicit dancer signup.
  const signInHref = applyTarget
    ? buildAuthUrlForApply(applyTarget, 'signin')
    : pathsConfig.auth.signIn;
  const signUpHref = applyTarget
    ? buildAuthUrlForApply(applyTarget, 'signup')
    : pathsConfig.auth.signUp;

  return (
    <div
      className={'animate-in fade-in flex items-center gap-x-2 duration-500'}
    >
      <LocaleSelector variant="header" selectedLocale={selectedLocale} />

      <div className={'hidden md:flex'}>
        <If condition={enableThemeToggle}>
          <ModeToggle />
        </If>
      </div>

      <div className={'md:hidden'}>
        <If condition={enableThemeToggle}>
          <MobileModeToggle />
        </If>
      </div>

      <div className={'flex items-center gap-x-2'}>
        <Button
          className={'hidden md:flex md:text-sm'}
          asChild
          variant={'outline'}
          size={'sm'}
        >
          <Link href={signInHref}>
            <Trans i18nKey={'auth:signIn'} />
          </Link>
        </Button>

        <Button
          asChild
          className="text-xs md:text-sm"
          variant={'default'}
          size={'sm'}
        >
          <Link href={signUpHref}>
            <Trans i18nKey={'auth:signUp'} />
          </Link>
        </Button>
      </div>
    </div>
  );
}
